import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { API } from "../api";
import { toasts } from "../components/AppToast";

import { useLocation, useNavigate } from "react-router-dom";
import {
  FiEdit,
  FiPlus,
  FiCheckCircle,
  FiArrowLeft,
  FiX,
  FiGlobe,
} from "react-icons/fi";

export default function AddSeo() {
  const routerLocation = useLocation();
  const navigate = useNavigate();

  const editSeo = routerLocation.state?.seo || null;
  const isEdit = Boolean(editSeo);

  // SIDEBAR & AUTH

  const [sideOpen, setSideOpen] = useState(true);
  const [activePage, setActivePage] = useState(
    isEdit ? "Edit SEO Details" : "Add SEO Details"
  );
  const [user, setUser] = useState(null);

  const [pages, setPages] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [parameters, setParameters] = useState([]);

  const [pageId, setPageId] = useState("");
  const [langId, setLangId] = useState("");

  const [seoValues, setSeoValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  // AUTH

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    setUser(JSON.parse(localStorage.getItem("user") || "null"));
  }, [navigate]);

  // LOAD MASTER DATA

  useEffect(() => {
    const loadData = async () => {
      try {
        setPageLoading(true);

        const [pageRes, langRes, paramRes] = await Promise.all([
          API.get("/seo/add-pages"),
          API.get("/seo/add-languages"),
          API.get("/seo/parameters"),
        ]);

        setPages(pageRes.data || []);
        setLanguages(langRes.data || []);
        setParameters(paramRes.data || []);
      } catch (error) {
        console.error("LOAD SEO MASTER DATA ERROR:", error);
        toasts.error("Failed to load SEO data");
      } finally {
        setPageLoading(false);
      }
    };

    loadData();
  }, []);

  // LOAD EDIT DATA

  useEffect(() => {
    const loadEditData = async () => {
      if (!isEdit || !editSeo) {
        return;
      }

      const id = editSeo.seodet_id ?? editSeo.seo_id ?? editSeo.id;

      if (!id) {
        toasts.error("SEO ID not found");
        return;
      }

      try {
        const response = await API.get(`/seo/edit/${id}`);

        if (!response.data?.success) {
          toasts.error("Failed to load SEO details");
          return;
        }

        const data = response.data;

        setPageId(
          data.page_id !== undefined && data.page_id !== null
            ? String(data.page_id)
            : ""
        );

        setLangId(
          data.lang_id !== undefined && data.lang_id !== null
            ? String(data.lang_id)
            : ""
        );

        const values = {};

        if (Array.isArray(data.parameters)) {
          data.parameters.forEach((item) => {
            const parameterId = Number(item.seo_parmid);

            if (!parameterId) {
              return;
            }

            values[parameterId] = item.seo_details || "";
          });
        }

        setSeoValues(values);
      } catch (error) {
        console.error("LOAD EDIT SEO ERROR:", error);
        toasts.error(
          error.response?.data?.message || "Failed to load SEO details"
        );
      }
    };

    loadEditData();
  }, [isEdit, editSeo]);

  // HANDLE SEO CHANGE

  const handleSeoChange = (parameterId, value) => {
    const id = Number(parameterId);

    setSeoValues((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  // VALIDATION

  const validateForm = () => {
    if (!pageId) {
      toasts.warning("Please select Page Name");
      return false;
    }

    if (!langId) {
      toasts.warning("Please select Language");
      return false;
    }

    for (const parameter of parameters) {
      const parameterId = Number(parameter.seo_parmid);

      if ([1, 2, 3].includes(parameterId)) {
        const value = seoValues[parameterId];

        if (!value || !String(value).trim()) {
          toasts.warning(`Please enter ${parameter.parameter_name}`);
          return false;
        }
      }
    }

    return true;
  };

  // SUBMIT

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const seoParameters = parameters.map((parameter) => {
        const parameterId = Number(parameter.seo_parmid);

        return {
          seo_parmid: parameterId,
          parameter_name: parameter.parameter_name,
          value: seoValues[parameterId] || "",
        };
      });

      const payload = {
        page_id: pageId,
        lang_id: langId,
        parameters: seoParameters,
      };

      if (isEdit) {
        const id = editSeo.seodet_id ?? editSeo.seo_id ?? editSeo.id;

        if (!id) {
          toasts.error("SEO ID not found");
          return;
        }

        await API.put(`/seo/${id}`, payload);
        toasts.success("SEO details updated successfully");
      } else {
        await API.post("/seo", payload);
        toasts.success("SEO details added successfully");
      }

      setTimeout(() => {
        navigate("/manage-seo");
      }, 1000);
    } catch (error) {
      console.error("SAVE SEO ERROR:", error);
      toasts.error(
        error.response?.data?.message || "Failed to save SEO details"
      );
    } finally {
      setLoading(false);
    }
  };

  // CANCEL

  const handleCancel = () => {
    navigate("/manage-seo");
  };

  // LOADING STATE

  if (pageLoading) {
    return (
      <Layout
        sideOpen={sideOpen}
        setSideOpen={setSideOpen}
        activePage={activePage}
        setActivePage={setActivePage}
        user={user}
      >
        <main className="dashboard-main">
          <div className="text-center py-5">
            <span className="spinner" />
            <div className="mt-2 text-muted">Loading SEO data...</div>
          </div>
        </main>
      </Layout>
    );
  }

  // RENDER

  return (
    <Layout
      sideOpen={sideOpen}
      setSideOpen={setSideOpen}
      activePage={activePage}
      setActivePage={setActivePage}
      user={user}
    >
      <main className="dashboard-main">
        <div className="user-form-page">
          {/* HEADER */}
          <div className="user-form-header">
            <div className="d-flex align-items-center gap-2">
              <div className="form-card-icon">
                {isEdit ? <FiEdit size={24} /> : <FiGlobe size={24} />}
              </div>
              <div>
                <h2 className="um-modal-title mb-0">
                  {isEdit ? "Edit SEO Details" : "Add New SEO Details"}
                </h2>
                <p className="form-card-sub mb-0">
                  {isEdit
                    ? "(Update SEO information)"
                    : "(Create a new SEO record)"}
                </p>
              </div>
            </div>

            <button
              type="button"
              className="um-btn-secondary"
              onClick={handleCancel}
            >
              <FiArrowLeft />
              Back to SEO
            </button>
          </div>

          {/* FORM CARD */}
          <div className="user-form-card">
            <form onSubmit={handleSubmit}>
              {/* BASIC CONFIGURATION SECTION */}
              <div className="sec-div">
                <span>Basic Configuration</span>
              </div>

              <div className="row g-4">
                {/* PAGE NAME */}
                <div className="col-xl-6 col-lg-6 col-md-6">
                  <label className="um-label">
                    Page Name <span className="text-danger">*</span>
                  </label>

                  <select
                    className="um-input"
                    value={pageId}
                    onChange={(e) => setPageId(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">Select Page</option>
                    {pages.map((page) => (
                      <option key={page.page_id} value={page.page_id}>
                        {page.page_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* LANGUAGE */}
                <div className="col-xl-6 col-lg-6 col-md-6">
                  <label className="um-label">
                    Language <span className="text-danger">*</span>
                  </label>

                  <select
                    className="um-input"
                    value={langId}
                    onChange={(e) => setLangId(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">Select Language</option>
                    {languages.map((language) => (
                      <option key={language.lang_id} value={language.lang_id}>
                        {language.lang_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* SEO PARAMETERS SECTION */}
              <div className="sec-div mt-4">
                <span>SEO Parameters</span>
              </div>

              <div className="row g-4">
                {parameters.map((parameter) => {
                  const parameterId = Number(parameter.seo_parmid);
                  const isRequired = [1, 2, 3].includes(parameterId);
                  const isTextarea = [2, 3].includes(parameterId);

                  return (
                    <div className="col-12" key={parameter.seo_parmid}>
                      <label className="um-label">
                        {parameter.parameter_name}
                        {isRequired && <span className="text-danger"> *</span>}
                      </label>

                      {isTextarea ? (
                        <textarea
                          className="um-input"
                          rows="4"
                          value={seoValues[parameterId] || ""}
                          onChange={(e) =>
                            handleSeoChange(parameterId, e.target.value)
                          }
                          disabled={loading}
                          placeholder={`Enter ${parameter.parameter_name.toLowerCase()}...`}
                        />
                      ) : (
                        <input
                          type="text"
                          className="um-input"
                          value={seoValues[parameterId] || ""}
                          onChange={(e) =>
                            handleSeoChange(parameterId, e.target.value)
                          }
                          disabled={loading}
                          placeholder={`Enter ${parameter.parameter_name.toLowerCase()}...`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* FORM FOOTER */}
              <div className="user-form-footer mt-4">
                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  <FiX />
                  Cancel
                </button>

                <button
                  type="submit"
                  className="um-btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner" />
                      Saving...
                    </>
                  ) : isEdit ? (
                    <>
                      <FiCheckCircle />
                      Update SEO
                    </>
                  ) : (
                    <>
                      <FiPlus />
                      Add SEO
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>

     
    </Layout>
  );
}
